/**
 * Static Pages - Todo en Local
 * Acerca de, Contacto, Privacidad, Términos, FAQ, Cómo Funciona
 * All using MarketplaceLayout for consistent header/footer/cart
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Send, ChevronDown, ChevronUp,
  Users, Target, Eye, Heart, Store, Truck, CreditCard,
  Shield, Star, Clock, Package, HelpCircle, Percent, Monitor,
  ShoppingBag, Receipt, Calculator, Briefcase, ArrowRight, Check,
  Zap, Link2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// ==================== ACERCA DE NOSOTROS ====================
export const AcercaPage = () => (
  <MarketplaceLayout>
    {/* Hero */}
    <section className="py-16" style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue}, ${COLORS.darkSlate})` }}>
      <div className="max-w-4xl mx-auto px-4 text-center text-white">
        <h1 className="text-4xl font-bold mb-4">Acerca de Todo en Local</h1>
        <p className="text-lg text-slate-300">
          Conectamos comercios locales con sus comunidades, impulsando la economía local de México.
        </p>
      </div>
    </section>

    {/* Story */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Nuestra Historia</h2>
            <p className="text-slate-600 mb-4">
              Todo en Local nació de la necesidad de conectar a los comercios locales mexicanos con la tecnología 
              que les permita competir en la era digital, sin perder su esencia y cercanía con sus clientes.
            </p>
            <p className="text-slate-600">
              Creemos que cada tienda de la esquina, cada mercado local y cada emprendedor merece las mismas 
              herramientas que las grandes cadenas, pero adaptadas a su realidad y necesidades.
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
                 style={{ background: `linear-gradient(135deg, ${COLORS.darkSlate}, ${COLORS.navyBlue})` }}>
              <Store className="w-10 h-10 text-white" />
            </div>
            <p className="text-3xl font-bold text-slate-800">+1,000</p>
            <p className="text-slate-600">Comercios Locales</p>
          </div>
        </div>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                 style={{ backgroundColor: `${COLORS.navyBlue}15` }}>
              <Target className="w-7 h-7" style={{ color: COLORS.navyBlue }} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-3">Misión</h3>
            <p className="text-slate-600">
              Empoderar a los comercios locales mexicanos con tecnología accesible y fácil de usar, 
              permitiéndoles digitalizar sus operaciones, llegar a más clientes y hacer crecer sus negocios 
              de manera sostenible.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                 style={{ backgroundColor: `${COLORS.orange}15` }}>
              <Eye className="w-7 h-7" style={{ color: COLORS.orange }} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-3">Visión</h3>
            <p className="text-slate-600">
              Ser la plataforma líder en México que transforme la forma en que las comunidades 
              compran y venden localmente, fortaleciendo las economías regionales y creando un 
              ecosistema comercial más justo y conectado.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-800 text-center mb-12">Nuestros Valores</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Heart, title: 'Comunidad', desc: 'Creemos en el poder de lo local y en fortalecer los lazos comunitarios.' },
            { icon: Shield, title: 'Confianza', desc: 'Construimos relaciones transparentes y honestas con todos nuestros usuarios.' },
            { icon: Star, title: 'Excelencia', desc: 'Nos esforzamos por ofrecer la mejor experiencia posible en cada interacción.' }
          ].map((value, idx) => (
            <div key={idx} className="text-center p-6">
              <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4"
                   style={{ backgroundColor: `${COLORS.navyBlue}15` }}>
                <value.icon className="w-6 h-6" style={{ color: COLORS.navyBlue }} />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">{value.title}</h3>
              <p className="text-sm text-slate-600">{value.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </MarketplaceLayout>
);

// ==================== CONTACTO ====================
export const ContactoPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Mensaje enviado correctamente. Te responderemos pronto.');
    setForm({ name: '', email: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <MarketplaceLayout>
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-slate-800 text-center mb-4">Contacto</h1>
          <p className="text-slate-600 text-center mb-12">
            ¿Tienes alguna pregunta o comentario? Estamos aquí para ayudarte.
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">Información de Contacto</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: `${COLORS.navyBlue}15` }}>
                    <Mail className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Email</p>
                    <a href="mailto:contacto@todoenlocal.com" className="text-slate-600 hover:text-orange-500">
                      contacto@todoenlocal.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: `${COLORS.orange}15` }}>
                    <Phone className="w-5 h-5" style={{ color: COLORS.orange }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Teléfono</p>
                    <a href="tel:+525512345678" className="text-slate-600 hover:text-orange-500">
                      +52 55 1234 5678
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: `${COLORS.navyBlue}15` }}>
                    <MapPin className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Dirección</p>
                    <p className="text-slate-600">Ciudad de México, México</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: `${COLORS.orange}10` }}>
                <h3 className="font-semibold text-slate-800 mb-2">Horario de Atención</h3>
                <p className="text-slate-600 text-sm">
                  Lunes a Viernes: 9:00 AM - 6:00 PM<br />
                  Sábados: 10:00 AM - 2:00 PM
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-800 mb-6">Envíanos un mensaje</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Asunto</label>
                  <input
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({...form, subject: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mensaje</label>
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  {sending ? 'Enviando...' : <><Send className="w-4 h-4" /> Enviar Mensaje</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

// ==================== CÓMO FUNCIONA ====================
export const ComoFuncionaPage = () => {
  const [activeTab, setActiveTab] = useState('comprador');

  const buyerSteps = [
    { step: 1, icon: MapPin, title: 'Explora tiendas locales', desc: 'Usa tu ubicacion para descubrir negocios cerca de ti. Navega por categorias, busca productos especificos o explora las tiendas destacadas.' },
    { step: 2, icon: Package, title: 'Encuentra lo que necesitas', desc: 'Compara precios, revisa disponibilidad y lee opiniones de otros compradores. Filtra por distancia, ofertas o tipo de entrega.' },
    { step: 3, icon: Store, title: 'Elige como recibirlo', desc: 'Recoge en tienda sin costo extra o solicita entrega a domicilio. Ve el costo de envio y el tiempo estimado antes de confirmar.' },
    { step: 4, icon: CreditCard, title: 'Paga como prefieras', desc: 'Paga en efectivo al recibir, con tarjeta de credito/debito, transferencia bancaria o SPEI. Tu compra siempre esta protegida.' },
  ];

  const buyerFeatures = [
    { icon: Heart, title: 'Favoritos', desc: 'Guarda tus productos y tiendas favoritas para encontrarlos rapido la proxima vez.' },
    { icon: Truck, title: 'Rastreo en tiempo real', desc: 'Sigue tu pedido en el mapa en vivo. Ve la ubicacion exacta de tu repartidor y el tiempo estimado de llegada.' },
    { icon: Monitor, title: 'Menu digital QR', desc: 'Escanea el QR de un restaurante y ve su menu completo desde tu celular. Personaliza tu pedido con modificadores y combos.' },
    { icon: Clock, title: 'Horarios en vivo', desc: 'Consulta si la tienda esta abierta o cerrada en este momento y a que hora abre o cierra.' },
    { icon: Shield, title: 'Compra protegida', desc: 'Todas las tiendas estan verificadas. Tu informacion personal y tus pagos estan protegidos.' },
    { icon: Star, title: 'Opiniones y calificaciones', desc: 'Lee resenas de otros compradores y califica tu experiencia para ayudar a la comunidad.' },
  ];

  const sellerSteps = [
    { step: 1, icon: Store, title: 'Registra tu negocio', desc: 'Crea tu perfil en minutos: nombre, logo, direccion, horarios y zonas de entrega. Configura multiples sucursales si lo necesitas.' },
    { step: 2, icon: Package, title: 'Sube tu catalogo', desc: 'Agrega productos uno por uno o cargalos masivamente con CSV. Incluye fotos, precios, SKU, codigo de barras y categorias.' },
    { step: 3, icon: ShoppingBag, title: 'Recibe pedidos', desc: 'Los pedidos del marketplace y del menu digital llegan en tiempo real a tu panel. Notificaciones instantaneas para no perder ninguna venta.' },
    { step: 4, icon: Truck, title: 'Entrega al cliente', desc: 'Gestiona tus repartidores, asigna pedidos y optimiza rutas automaticamente. El cliente rastrea su entrega en vivo.' },
  ];

  const sellerModules = [
    {
      title: 'Punto de Venta (POS)',
      icon: Monitor,
      color: COLORS.navyBlue,
      items: [
        'Terminal de venta con interfaz tactil',
        'Ventas con efectivo, tarjeta y transferencia',
        'Gestion de cajeros con PIN de seguridad',
        'Multiples cajas registradoras por sucursal',
        'Modo offline: vende sin internet y sincroniza despues',
        'Cortes de caja y reportes por turno',
      ]
    },
    {
      title: 'Menu Digital',
      icon: Zap,
      color: '#8b5cf6',
      items: [
        'Menu online accesible por codigo QR',
        'URL personalizada para tu negocio',
        'Pedidos desde el celular del cliente',
        'Modificadores y combos para restaurantes (Comanda Pro)',
        'Instrucciones especiales por producto',
        'Notificaciones instantaneas de nuevos pedidos',
      ]
    },
    {
      title: 'Inventario y Productos',
      icon: Package,
      color: '#059669',
      items: [
        'Control de stock por sucursal en tiempo real',
        'Alertas de inventario bajo',
        'Precios de mayoreo con 3 niveles configurables',
        'Precio de comparacion para mostrar descuentos',
        'Carga masiva de productos por CSV',
        'Imagenes optimizadas automaticamente',
      ]
    },
    {
      title: 'Entregas y Logistica',
      icon: Truck,
      color: COLORS.orange,
      items: [
        'Gestion completa de repartidores',
        'App dedicada para repartidores',
        'Optimizacion de rutas automatica',
        'Rastreo GPS en tiempo real',
        'Chat repartidor-cliente durante entrega',
        'Comprobante de entrega con foto',
      ]
    },
    {
      title: 'Facturacion y Finanzas',
      icon: Receipt,
      color: '#dc2626',
      items: [
        'Facturacion electronica CFDI integrada',
        'Autofacturacion para tus clientes con QR',
        'Configuracion de impuestos (IVA, IEPS)',
        'Codigos SAT por producto',
        'Reportes de ventas por periodo y metodo de pago',
        'Historial completo de transacciones',
      ]
    },
    {
      title: 'Gestion del Negocio',
      icon: Briefcase,
      color: '#0284c7',
      items: [
        'Multiples sucursales desde un solo panel',
        'Horarios de operacion por dia de la semana',
        'Cierre temporal sin afectar el horario',
        'Gestion de personal y cajeros',
        'Zonas de entrega con tarifas diferenciadas',
        'Reportes y analiticas de ventas',
      ]
    },
  ];

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: COLORS.darkSlate }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white/5" />
        <div className="max-w-5xl mx-auto px-4 py-16 relative z-10 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            ¿Como funciona <span style={{ color: COLORS.orange }}>Todo en Local</span>?
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            La plataforma que conecta a compradores con las mejores tiendas locales de su comunidad. Compra, vende y gestiona todo desde un solo lugar.
          </p>
          {/* Tab switcher */}
          <div className="inline-flex bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('comprador')}
              className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all ${
                activeTab === 'comprador' ? 'bg-white text-slate-800 shadow' : 'text-white/70 hover:text-white'
              }`}
            >
              Soy Comprador
            </button>
            <button
              onClick={() => setActiveTab('vendedor')}
              className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all ${
                activeTab === 'vendedor' ? 'text-white shadow' : 'text-white/70 hover:text-white'
              }`}
              style={activeTab === 'vendedor' ? { backgroundColor: COLORS.orange } : {}}
            >
              Soy Vendedor
            </button>
          </div>
        </div>
      </section>

      {/* ============ COMPRADOR ============ */}
      {activeTab === 'comprador' && (
        <>
          {/* Steps */}
          <section className="py-14 bg-white">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Comprar es muy facil</h2>
              <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">En 4 simples pasos puedes recibir productos de tiendas locales en tu domicilio o recogerlos en tienda.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {buyerSteps.map((item) => (
                  <div key={item.step} className="relative bg-gray-50 rounded-lg p-5">
                    <span className="absolute -top-3 -left-1 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs"
                          style={{ backgroundColor: COLORS.orange }}>
                      {item.step}
                    </span>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                         style={{ backgroundColor: `${COLORS.navyBlue}12` }}>
                      <item.icon className="w-6 h-6" style={{ color: COLORS.navyBlue }} />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Buyer features grid */}
          <section className="py-14">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Lo que puedes hacer</h2>
              <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">Disfruta de una experiencia de compra completa, desde buscar hasta recibir tu pedido en la puerta de tu casa.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {buyerFeatures.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-5 shadow-sm flex gap-4">
                    <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                         style={{ backgroundColor: `${COLORS.navyBlue}10` }}>
                      <item.icon className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1 text-sm">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA buyer */}
          <section className="py-12 bg-white">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h3 className="text-xl font-bold text-slate-800 mb-3">Empieza a comprar ahora</h3>
              <p className="text-slate-500 mb-6 text-sm">Crea tu cuenta gratis y descubre los mejores productos de tiendas locales cerca de ti.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/auth" className="inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition-all" style={{ backgroundColor: COLORS.navyBlue }}>
                  Crear cuenta gratis
                </Link>
                <Link to="/buscar" className="inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-lg font-semibold text-sm border border-gray-200 text-slate-700 hover:bg-gray-50 transition-all">
                  Explorar productos <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ============ VENDEDOR ============ */}
      {activeTab === 'vendedor' && (
        <>
          {/* Steps */}
          <section className="py-14 bg-white">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Empieza a vender en minutos</h2>
              <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">Registra tu negocio, sube tus productos y comienza a recibir pedidos del marketplace y de tu menu digital.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {sellerSteps.map((item) => (
                  <div key={item.step} className="relative bg-gray-50 rounded-lg p-5">
                    <span className="absolute -top-3 -left-1 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs"
                          style={{ backgroundColor: COLORS.orange }}>
                      {item.step}
                    </span>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                         style={{ backgroundColor: `${COLORS.orange}12` }}>
                      <item.icon className="w-6 h-6" style={{ color: COLORS.orange }} />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Seller modules */}
          <section className="py-14">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Todo lo que necesitas para tu negocio</h2>
              <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">Una plataforma completa con herramientas profesionales para vender mas y gestionar mejor.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sellerModules.map((module, idx) => (
                  <div key={idx} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${module.color}12` }}>
                          <module.icon className="w-5 h-5" style={{ color: module.color }} />
                        </div>
                        <h3 className="font-bold text-slate-800">{module.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {module.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: module.color }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Business types */}
          <section className="py-14 bg-white">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Para todo tipo de negocios</h2>
              <p className="text-slate-500 text-center mb-10 max-w-xl mx-auto">No importa tu giro, Todo en Local se adapta a las necesidades de tu negocio.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { emoji: '🛒', name: 'Retail y Tiendas' },
                  { emoji: '🍽️', name: 'Restaurantes' },
                  { emoji: '💈', name: 'Servicios' },
                  { emoji: '🏨', name: 'Hospedaje' },
                  { emoji: '🚗', name: 'Automotriz' },
                  { emoji: '🏠', name: 'Bienes Raices' },
                ].map((type, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-all">
                    <span className="text-3xl mb-2 block">{type.emoji}</span>
                    <span className="text-xs font-medium text-slate-700">{type.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA seller */}
          <section className="py-14" style={{ backgroundColor: COLORS.darkSlate }}>
            <div className="max-w-3xl mx-auto px-4 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">Lleva tu negocio al siguiente nivel</h3>
              <p className="text-slate-400 mb-8 max-w-lg mx-auto text-sm">
                Unete a Todo en Local y accede a miles de clientes en tu zona. Registrarte es gratis, rapido y sin compromisos.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register" className="inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition-all" style={{ backgroundColor: COLORS.orange }}>
                  Registrar mi Tienda
                </Link>
                <Link to="/precios" className="inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-lg font-semibold text-sm border border-white/20 text-white hover:bg-white/10 transition-all">
                  Ver planes y precios <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </MarketplaceLayout>
  );
};

// ==================== FAQ ====================
export const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Para Compradores',
      questions: [
        { q: '¿Cómo puedo buscar productos cerca de mí?', a: 'Activa tu ubicación en el navegador y la plataforma te mostrará automáticamente los productos y tiendas más cercanas a ti.' },
        { q: '¿Cuáles son las formas de pago disponibles?', a: 'Por el momento aceptamos pago en efectivo y con tarjeta directamente en la tienda al recoger tu pedido o al recibirlo a domicilio.' },
        { q: '¿Puedo devolver un producto?', a: 'Las políticas de devolución varían según cada tienda. Te recomendamos revisar los términos de la tienda antes de comprar o contactarlos directamente.' },
        { q: '¿Cómo sé si una tienda es confiable?', a: 'Las tiendas verificadas tienen una insignia especial. También puedes revisar las calificaciones y reseñas de otros compradores.' }
      ]
    },
    {
      category: 'Para Vendedores',
      questions: [
        { q: '¿Cuánto cuesta registrar mi tienda?', a: 'El registro básico es gratuito. Contamos con diferentes planes según las funcionalidades que necesites.' },
        { q: '¿Cómo recibo los pedidos?', a: 'Los pedidos llegan directamente a tu sistema Punto de Venta (POS) con una notificación sonora. Desde ahí puedes gestionarlos.' },
        { q: '¿Puedo vender al mayoreo?', a: 'Sí, puedes habilitar la opción de mayoreo en la configuración de tu tienda y establecer precios especiales para compras grandes.' },
        { q: '¿Cómo funciona la verificación de tienda?', a: 'La verificación incluye validación de documentos fiscales y comerciales. Una vez verificado, tu tienda obtiene una insignia que genera más confianza en los compradores.' }
      ]
    }
  ];

  return (
    <MarketplaceLayout>
      <div className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-slate-800 text-center mb-4">Preguntas Frecuentes</h1>
          <p className="text-slate-600 text-center mb-12">
            Encuentra respuestas a las dudas más comunes sobre Todo en Local.
          </p>

          {faqs.map((section, sectionIdx) => (
            <div key={sectionIdx} className="mb-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                {section.category}
              </h2>
              <div className="space-y-2">
                {section.questions.map((faq, idx) => {
                  const globalIdx = sectionIdx * 10 + idx;
                  return (
                    <div key={idx} className="bg-white rounded-lg border overflow-hidden">
                      <button
                        onClick={() => setOpenIndex(openIndex === globalIdx ? null : globalIdx)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left"
                      >
                        <span className="font-medium text-slate-800">{faq.q}</span>
                        {openIndex === globalIdx ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      {openIndex === globalIdx && (
                        <div className="px-4 pb-4 text-slate-600 text-sm">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-12 p-6 rounded-xl text-center" style={{ backgroundColor: `${COLORS.orange}10` }}>
            <h3 className="font-semibold text-slate-800 mb-2">¿No encontraste lo que buscabas?</h3>
            <p className="text-slate-600 text-sm mb-4">
              Nuestro equipo de soporte está listo para ayudarte.
            </p>
            <Link 
              to="/contacto"
              className="inline-flex items-center gap-2 px-6 py-2 rounded-full text-white font-medium"
              style={{ backgroundColor: COLORS.navyBlue }}
            >
              <Mail className="w-4 h-4" /> Contactar Soporte
            </Link>
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

// ==================== SHARED LEGAL SECTION COMPONENT ====================
const LegalSection = ({ id, number, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 md:gap-4">
        <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${COLORS.navyBlue}10` }}>
          <Icon className="w-4 h-4 md:w-5 md:h-5" style={{ color: COLORS.navyBlue }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-2 md:mb-3 flex items-center gap-2">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: COLORS.orange }}>{number}</span>
            <span>{title}</span>
          </h2>
          <div className="text-slate-600 text-sm md:text-[15px] leading-relaxed space-y-3">
            {children}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Table of contents nav
const LegalNav = ({ sections, activeSection }) => (
  <nav className="hidden lg:block sticky top-20 bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Contenido</p>
    <ul className="space-y-1">
      {sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            className={`block px-3 py-1.5 rounded-lg text-sm transition-colors ${
              activeSection === s.id
                ? 'font-medium text-white'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
            style={activeSection === s.id ? { backgroundColor: COLORS.navyBlue } : {}}
          >
            {s.number}. {s.title}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

// Hook for active section tracking
const useActiveSection = (sectionIds) => {
  const [active, setActive] = React.useState(sectionIds[0] || '');
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sectionIds]);
  return active;
};

// ==================== PRIVACIDAD ====================
const privacySections = [
  { id: 'info-recopilamos', number: 1, title: 'Información que Recopilamos' },
  { id: 'uso-info', number: 2, title: 'Uso de la Información' },
  { id: 'compartir-info', number: 3, title: 'Compartir Información' },
  { id: 'seguridad-datos', number: 4, title: 'Seguridad de los Datos' },
  { id: 'tus-derechos', number: 5, title: 'Tus Derechos' },
  { id: 'cookies', number: 6, title: 'Cookies' },
  { id: 'contacto-privacidad', number: 7, title: 'Contacto' },
];

export const PrivacidadPage = () => {
  const activeSection = useActiveSection(privacySections.map(s => s.id));

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue} 0%, ${COLORS.darkSlate} 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full border-2 border-white/20" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur rounded-full text-white/80 text-sm mb-5">
            <Shield className="w-4 h-4" />
            Protección de datos
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Política de Privacidad</h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            Tu privacidad es importante para nosotros. Aquí explicamos cómo recopilamos, usamos y protegemos tu información personal.
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Última actualización: Febrero 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">7 secciones</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <div className="flex gap-8">
          {/* Sidebar nav */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <LegalNav sections={privacySections} activeSection={activeSection} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-4">
            <LegalSection id="info-recopilamos" number={1} icon={Users} title="Información que Recopilamos">
              <p>
                En Todo en Local recopilamos información que nos proporcionas directamente, como tu nombre,
                correo electrónico, teléfono y dirección cuando creas una cuenta o realizas un pedido.
              </p>
              <p>
                También recopilamos automáticamente cierta información cuando usas nuestra plataforma,
                incluyendo tu ubicación (con tu consentimiento), dirección IP y datos de uso.
              </p>
            </LegalSection>

            <LegalSection id="uso-info" number={2} icon={Target} title="Uso de la Información">
              <p>Utilizamos la información recopilada para:</p>
              <ul className="list-none space-y-2 ml-0">
                {[
                  'Procesar tus pedidos y transacciones',
                  'Mostrarte tiendas y productos cercanos a tu ubicación',
                  'Enviarte notificaciones sobre el estado de tus pedidos',
                  'Mejorar nuestros servicios y experiencia de usuario',
                  'Comunicarnos contigo sobre actualizaciones o promociones (con tu consentimiento)'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: COLORS.orange }} />
                    {item}
                  </li>
                ))}
              </ul>
            </LegalSection>

            <LegalSection id="compartir-info" number={3} icon={Users} title="Compartir Información">
              <p>
                Compartimos tu información con las tiendas únicamente cuando realizas un pedido,
                para que puedan procesarlo y entregarlo. <strong>No vendemos tu información personal a terceros.</strong>
              </p>
            </LegalSection>

            <LegalSection id="seguridad-datos" number={4} icon={Shield} title="Seguridad de los Datos">
              <p>
                Implementamos medidas de seguridad técnicas y organizativas para proteger tu información
                personal contra acceso no autorizado, alteración, divulgación o destrucción.
              </p>
            </LegalSection>

            <LegalSection id="tus-derechos" number={5} icon={Star} title="Tus Derechos">
              <p>
                Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP),
                tienes derecho a acceder, rectificar, cancelar u oponerte al tratamiento de tus datos personales (derechos ARCO).
              </p>
            </LegalSection>

            <LegalSection id="cookies" number={6} icon={Eye} title="Cookies">
              <p>
                Utilizamos cookies y tecnologías similares para mejorar tu experiencia, analizar el uso
                de nuestra plataforma y personalizar el contenido.
              </p>
            </LegalSection>

            <LegalSection id="contacto-privacidad" number={7} icon={Mail} title="Contacto">
              <p>
                Para ejercer tus derechos ARCO o si tienes preguntas sobre esta política, contacta a nuestro
                oficial de protección de datos en:
              </p>
              <a
                href="mailto:privacidad@todoenlocal.com"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white mt-1"
                style={{ backgroundColor: COLORS.navyBlue }}
              >
                <Mail className="w-4 h-4" />
                privacidad@todoenlocal.com
              </a>
            </LegalSection>

            {/* Bottom CTA */}
            <div className="rounded-xl p-6 text-center mt-6" style={{ backgroundColor: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}20` }}>
              <p className="text-slate-700 font-medium mb-1">¿Tienes dudas sobre tu privacidad?</p>
              <p className="text-sm text-slate-500 mb-4">Nuestro equipo está disponible para responder tus preguntas.</p>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium"
                style={{ backgroundColor: COLORS.orange }}
              >
                <Mail className="w-4 h-4" /> Contactar Soporte
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

// ==================== TÉRMINOS Y CONDICIONES ====================
const termsSections = [
  { id: 'aceptacion', number: 1, title: 'Aceptación de los Términos' },
  { id: 'descripcion-servicio', number: 2, title: 'Descripción del Servicio' },
  { id: 'registro-usuarios', number: 3, title: 'Registro de Usuarios' },
  { id: 'responsabilidades-vendedores', number: 4, title: 'Responsabilidades de los Vendedores' },
  { id: 'transacciones-pagos', number: 5, title: 'Transacciones y Pagos' },
  { id: 'devoluciones', number: 6, title: 'Devoluciones y Cancelaciones' },
  { id: 'propiedad-intelectual', number: 7, title: 'Propiedad Intelectual' },
  { id: 'limitacion-responsabilidad', number: 8, title: 'Limitación de Responsabilidad' },
  { id: 'modificaciones', number: 9, title: 'Modificaciones' },
  { id: 'ley-aplicable', number: 10, title: 'Ley Aplicable' },
];

export const TerminosPage = () => {
  const activeSection = useActiveSection(termsSections.map(s => s.id));

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLORS.darkSlate} 0%, ${COLORS.navyBlue} 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-40 h-40 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-10 left-20 w-60 h-60 rounded-full border-2 border-white/20" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur rounded-full text-white/80 text-sm mb-5">
            <Shield className="w-4 h-4" />
            Marco legal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Términos y Condiciones</h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            Estos términos regulan el uso de la plataforma Todo en Local. Al utilizarla, aceptas estas condiciones.
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Última actualización: Febrero 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">10 secciones</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <div className="flex gap-8">
          {/* Sidebar nav */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <LegalNav sections={termsSections} activeSection={activeSection} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-4">
            <LegalSection id="aceptacion" number={1} icon={Shield} title="Aceptación de los Términos">
              <p>
                Al acceder y utilizar la plataforma Todo en Local, aceptas estar sujeto a estos términos y condiciones.
                Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestros servicios.
              </p>
            </LegalSection>

            <LegalSection id="descripcion-servicio" number={2} icon={Store} title="Descripción del Servicio">
              <p>
                Todo en Local es una plataforma que conecta comercios locales con compradores, permitiendo
                la búsqueda de productos, comparación de precios y realización de pedidos.
              </p>
            </LegalSection>

            <LegalSection id="registro-usuarios" number={3} icon={Users} title="Registro de Usuarios">
              <p>
                Para realizar compras o registrar un negocio, debes crear una cuenta proporcionando información
                veraz y actualizada. Eres responsable de mantener la confidencialidad de tu cuenta.
              </p>
            </LegalSection>

            <LegalSection id="responsabilidades-vendedores" number={4} icon={Store} title="Responsabilidades de los Vendedores">
              <p>Los comercios registrados en la plataforma son responsables de:</p>
              <ul className="list-none space-y-2 ml-0">
                {[
                  'Mantener actualizado su inventario y precios',
                  'Cumplir con los pedidos en los tiempos establecidos',
                  'Ofrecer productos que cumplan con las descripciones publicadas',
                  'Cumplir con todas las leyes y regulaciones aplicables'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: COLORS.orange }} />
                    {item}
                  </li>
                ))}
              </ul>
            </LegalSection>

            <LegalSection id="transacciones-pagos" number={5} icon={CreditCard} title="Transacciones y Pagos">
              <p>
                Todo en Local facilita la conexión entre compradores y vendedores. Los pagos se realizan
                directamente en el establecimiento o al momento de la entrega. No procesamos pagos en línea
                actualmente.
              </p>
            </LegalSection>

            <LegalSection id="devoluciones" number={6} icon={Package} title="Devoluciones y Cancelaciones">
              <p>
                Las políticas de devolución son establecidas por cada comercio. Los compradores deben
                revisar las políticas específicas de cada tienda antes de realizar un pedido.
              </p>
            </LegalSection>

            <LegalSection id="propiedad-intelectual" number={7} icon={Star} title="Propiedad Intelectual">
              <p>
                Todo el contenido de la plataforma, incluyendo logos, diseños y software, es propiedad de
                Todo en Local y está protegido por las leyes de propiedad intelectual.
              </p>
            </LegalSection>

            <LegalSection id="limitacion-responsabilidad" number={8} icon={Shield} title="Limitación de Responsabilidad">
              <p>
                Todo en Local actúa como intermediario y no es responsable por la calidad de los productos,
                retrasos en entregas o disputas entre compradores y vendedores.
              </p>
            </LegalSection>

            <LegalSection id="modificaciones" number={9} icon={Clock} title="Modificaciones">
              <p>
                Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios
                serán notificados a través de la plataforma.
              </p>
            </LegalSection>

            <LegalSection id="ley-aplicable" number={10} icon={Shield} title="Ley Aplicable">
              <p>
                Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia
                será sometida a los tribunales competentes de la Ciudad de México.
              </p>
            </LegalSection>

            {/* Bottom CTA */}
            <div className="rounded-xl p-6 text-center mt-6" style={{ backgroundColor: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}20` }}>
              <p className="text-slate-700 font-medium mb-1">¿Tienes preguntas sobre estos términos?</p>
              <p className="text-sm text-slate-500 mb-4">Estamos para aclarar cualquier duda que tengas.</p>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium"
                style={{ backgroundColor: COLORS.orange }}
              >
                <Mail className="w-4 h-4" /> Contactar Soporte
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

// ==================== PRECIOS ====================
const enLocalProducts = [
  {
    name: 'enLocal Comanda Pro',
    desc: 'Sistema de comandas y gestión de pedidos para restaurantes y cocinas.',
    icon: Monitor,
    syncs: true,
    features: ['Gestión de comandas en tiempo real', 'Pantalla de cocina', 'Sincronización con TodoEnLocal', 'Menú Digital (addon)'],
    color: '#F37322',
    link: '/comanda-pro',
    addon: 'Menú Digital'
  },
  {
    name: 'enLocal Caja',
    desc: 'Punto de venta completo para el control de ventas, inventario y clientes.',
    icon: ShoppingBag,
    syncs: true,
    features: ['Punto de venta táctil', 'Control de inventario', 'Sincronización con TodoEnLocal', 'Reportes de ventas'],
    color: '#1e3a5f'
  },
  {
    name: 'enLocal Servicios',
    desc: 'Gestión de servicios profesionales: citas, agendas y seguimiento de clientes.',
    icon: Briefcase,
    syncs: true,
    features: ['Agenda de citas', 'Catálogo de servicios', 'Sincronización con TodoEnLocal', 'Historial de clientes'],
    color: '#7c3aed'
  },
  {
    name: 'enLocal ERP',
    desc: 'Sistema integral de gestión empresarial para operaciones avanzadas.',
    icon: Calculator,
    syncs: false,
    features: ['Gestión de compras', 'Control de almacén', 'Cuentas por cobrar/pagar', 'Reportes financieros'],
    color: '#0891b2'
  },
  {
    name: 'enLocal Factura',
    desc: 'Facturación electrónica CFDI 4.0 integrada y cumplimiento fiscal.',
    icon: Receipt,
    syncs: false,
    features: ['CFDI 4.0', 'Complementos de pago', 'Cancelación de facturas', 'Reportes fiscales'],
    color: '#059669'
  },
  {
    name: 'enLocal Nóminas',
    desc: 'Cálculo y timbrado de nómina, control de empleados y prestaciones.',
    icon: Users,
    syncs: false,
    features: ['Timbrado de nómina', 'Control de asistencia', 'Cálculo de prestaciones', 'Reportes IMSS/INFONAVIT'],
    color: '#dc2626'
  }
];

export const PreciosPage = () => {
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue} 0%, ${COLORS.darkSlate} 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-6 left-10 w-32 h-32 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-6 right-16 w-52 h-52 rounded-full border-2 border-white/20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-white/10" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur rounded-full text-white/80 text-sm mb-5">
            <Percent className="w-4 h-4" />
            Transparente y accesible
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Precios</h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            Vender en TodoEnLocal es sencillo. Sin mensualidades ni costos ocultos, solo pagas una comisión por venta realizada.
          </p>
        </div>
      </section>

      {/* Marketplace Commission */}
      <section className="max-w-5xl mx-auto px-4 -mt-10 relative z-10 mb-12">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="md:flex">
            {/* Left - Commission */}
            <div className="flex-1 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}15` }}>
                  <Store className="w-6 h-6" style={{ color: COLORS.orange }} />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-slate-800">TodoEnLocal Marketplace</h2>
                  <p className="text-sm text-slate-500">Comisión por venta</p>
                </div>
              </div>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-5xl md:text-6xl font-extrabold" style={{ color: COLORS.navyBlue }}>6.5</span>
                <span className="text-2xl md:text-3xl font-bold" style={{ color: COLORS.navyBlue }}>%</span>
                <span className="text-slate-500 ml-2 text-lg">por venta</span>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  'Sin mensualidades ni costos fijos',
                  'Publicación gratuita de productos',
                  'Panel de gestión de pedidos incluido',
                  'Soporte técnico incluido',
                  'Reportes de ventas en tiempo real',
                  'Solo pagas cuando vendes'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-700 text-sm md:text-base">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.orange }} />
                    {item}
                  </li>
                ))}
              </ul>

              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm transition-transform hover:scale-105"
                style={{ backgroundColor: COLORS.orange }}
              >
                Comenzar a vender
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Right - How it works */}
            <div className="bg-slate-50 p-6 md:p-10 md:w-80 lg:w-96 border-t md:border-t-0 md:border-l border-slate-100">
              <h3 className="font-bold text-slate-800 mb-4">¿Cómo funciona?</h3>
              <div className="space-y-4">
                {[
                  { step: 1, title: 'Registra tu negocio', desc: 'Crea tu perfil y sube tu catálogo gratis.' },
                  { step: 2, title: 'Recibe pedidos', desc: 'Los clientes encuentran tus productos y te compran.' },
                  { step: 3, title: 'Cobra y entrega', desc: 'Prepara el pedido. Solo se cobra el 6.5% de comisión.' }
                ].map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                         style={{ backgroundColor: COLORS.navyBlue }}>
                      {item.step}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{item.title}</p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-xl bg-white border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Ejemplo de venta</p>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">Venta de</span>
                  <span className="font-bold text-slate-800">$1,000.00</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-slate-600">Comisión (6.5%)</span>
                  <span className="font-medium text-red-500">-$65.00</span>
                </div>
                <div className="border-t mt-2 pt-2 flex justify-between items-center">
                  <span className="font-semibold text-slate-800 text-sm">Tú recibes</span>
                  <span className="font-bold text-lg" style={{ color: COLORS.navyBlue }}>$935.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* enLocal Software Suite */}
      <section className="max-w-5xl mx-auto px-4 mb-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 rounded-full text-slate-600 text-sm mb-4">
            <Zap className="w-4 h-4" style={{ color: COLORS.orange }} />
            Ecosistema enLocal
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Software de la familia enLocal</h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base">
            Potencia tu negocio con nuestro ecosistema de software. Los productos marcados con
            <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
              <Link2 className="w-3 h-3" /> Sincroniza
            </span>
            se conectan directamente con TodoEnLocal para publicar tus productos y servicios automáticamente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {enLocalProducts.map((product) => (
            <div key={product.name} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-5 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${product.color}12` }}>
                    <product.icon className="w-5 h-5" style={{ color: product.color }} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {product.syncs && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                        <Link2 className="w-3 h-3" /> Sincroniza
                      </span>
                    )}
                    {product.addon && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        + {product.addon}
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-bold text-slate-800 text-base mb-1">{product.name}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{product.desc}</p>
              </div>
              <div className="px-5 pb-5">
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: product.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/50">
                {product.link ? (
                  <Link
                    to={product.link}
                    className="flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                    style={{ color: product.color }}
                  >
                    Ver más
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <a
                    href="https://todoenlocal.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                    style={{ color: product.color }}
                  >
                    Más información
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16" style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue} 0%, ${COLORS.darkSlate} 100%)` }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">¿Listo para vender en TodoEnLocal?</h2>
          <p className="text-slate-300 mb-8 text-sm md:text-base">
            Registra tu negocio gratis y comienza a llegar a más clientes hoy mismo.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm transition-transform hover:scale-105"
              style={{ backgroundColor: COLORS.orange }}
            >
              Comenzar ahora
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/como-funciona"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm bg-white/10 backdrop-blur hover:bg-white/20 transition-colors"
            >
              ¿Cómo funciona?
            </Link>
          </div>
        </div>
      </section>
    </MarketplaceLayout>
  );
};
